import React, { SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { confirmResetPassword } from '../../actions/resetPasswordActions';
import ErrorResponse from '../../components/ErrorResponse';
import Input from '../../components/Input';
import PrimaryButton from '../../components/PrimaryButton';
import { FormWrapper } from '../../components/ViewWrapper';
import { PASSWORD_REGEX } from '../../constants';
import logo from '../../img/Airthings_Favicon.png';
import { Store } from '../../reducers';
import { RequestType } from '../../reducers/requestReducer';
import styles from '../registerUser/RegisterView.module.scss';

type Props = {
    email: string;
};

const SetNewPassword = ({ email }: Props): JSX.Element => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();
    const { loading: confirmResetLoading, error } = useSelector(
        (state: Store) => state.requests[RequestType.ConfirmResetPassword]
    );
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirmedPassword, setConfirmedPassword] = useState('');
    const [validateInput, setValidateInput] = useState(false);

    const onResetPassword = (e: SyntheticEvent<HTMLFormElement> | SyntheticEvent<HTMLButtonElement>): void => {
        e.preventDefault();
        e.stopPropagation();
        const inputValid = PASSWORD_REGEX.test(password) && confirmedPassword === password && code.length > 0;
        if (inputValid) {
            dispatch(confirmResetPassword({ email, password, confirmationCode: code }));
        } else {
            setValidateInput(true);
        }
    };

    const onChange = (value: string, key: string): void => {
        if (key === 'code') {
            setCode(value.trim());
        } else if (key === 'password') {
            setPassword(value);
        } else if (key === 'confirmPassword') {
            setConfirmedPassword(value);
        }
    };

    return (
        <FormWrapper onSubmit={onResetPassword} testAttr="set-new-password">
            <img src={logo} className={styles.logo} alt={txt('Logo.AirthingsLogo')} />
            <Input label="ResetPassword.UniqueCode" value={code} type="number" onChange={onChange} id="code" required />
            <Input
                label="User.Password"
                type="password"
                id="password"
                required
                invalid={validateInput && !PASSWORD_REGEX.test(password)}
                helpText="Register.PasswordHint"
                alwaysShowHelpText
                autoComplete="off"
                onChange={onChange}
                value={password}
            />
            <Input
                label="Register.ReEnterPassword"
                type="password"
                id="confirmPassword"
                required
                helpText="Register.PasswordConfirmHint"
                autoComplete="off"
                invalid={validateInput && password !== confirmedPassword}
                onChange={onChange}
                value={confirmedPassword}
            />
            {error && <ErrorResponse error={`ErrorCodes.${error.error}`} testAttr={'reset-password-error'} />}
            <PrimaryButton
                onClick={onResetPassword}
                loading={confirmResetLoading}
                title={'ResetPassword.SavePassword'}
            />
        </FormWrapper>
    );
};

export default SetNewPassword;
